<template>
  <main class="content">
    <div class="container-fluid p-0">
      <h1 class="h3 mb-3">Shopping Cart</h1>

      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <empty-cart v-else-if="!hasItems" />

      <div v-else class="row">
        <div class="col-lg-8">
          <!-- Own styling, not .alert: the theme's .alert has no padding (it expects an .alert-message
               child), so the text ran into the box's edge (2026-10-21). -->
          <div v-if="pricing.unavailableItems.length > 0" role="alert" class="mb-3" style="padding:12px 14px;background:#fff6e0;border:1px solid #f1d79c;border-radius:10px;color:#5c3d00;font-size:.92rem;line-height:1.4;">
            Some items in your cart are no longer available. Please remove them to continue.
          </div>
          <!-- Only increases warrant a banner; drops show as a green chip on the item. -->
          <div v-if="priceWentUp" role="alert" class="mb-3" style="padding:12px 14px;background:#fff6e0;border:1px solid #f1d79c;border-radius:10px;color:#5c3d00;font-size:.92rem;line-height:1.4;">
            A price went up since you added it to your cart. The new price is shown below.
          </div>

          <!-- Phones with 2+ shops (2026-10-30): how the total is made, up top, with the way to checkout.
               Since September 7 of 36 carts held 3+ shops and none was paid. -->
          <div v-if="foldShops" class="mb-3 p-3 bg-white" data-track-area="cart.boxes-summary" style="border:1px solid #e5e8ef;border-radius:12px;">
            <div style="font-size:.85rem;font-weight:700;color:#475467;">{{ pricing.sellerGroups.length }} boxes from {{ pricing.sellerGroups.length }} shops</div>
            <div class="d-flex justify-content-between mt-2"><span>Items</span><strong>${{ pricing.grandSubtotal.toFixed(2) }}</strong></div>
            <div class="d-flex justify-content-between mt-1"><span>Shipping, one box per shop</span><strong>${{ pricing.grandShipping.toFixed(2) }}</strong></div>
            <div class="d-flex justify-content-between mt-2 pt-2" style="border-top:1px solid #eef0f4;font-size:1.05rem;font-weight:800;"><span>Items + shipping</span><span>${{ (pricing.grandSubtotal + pricing.grandShipping).toFixed(2) }}</span></div>
            <div class="small text-muted mt-1">Each shop packs and ships its own box. Tap a shop to see its items.</div>
            <a href="/checkout" class="btn btn-primary btn-lg btn-block mt-3" :class="{ disabled: pricing.unavailableItems.length > 0 }" :tabindex="pricing.unavailableItems.length > 0 ? -1 : 0" style="font-weight:800;">Check out</a>
          </div>

          <!-- Second shop, second box (SecondShop, 2026-10-21): why there's more than one shipping charge. -->
          <div v-if="pricing.sellerGroups.length > 1 && !foldShops" class="mb-3 p-3 d-flex bg-white" style="border:1px solid #d6e2f5;border-radius:10px;gap:10px;font-size:.92rem;line-height:1.4;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333957" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;margin-top:2px;"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><path d="M3.3 7 12 12l8.7-5"></path><path d="M12 22V12"></path></svg>
            <div><strong>{{ pricing.sellerGroups.length }} shops, {{ pricing.sellerGroups.length }} boxes.</strong> Each shop packs its own insulated box and ships it fast, so each one has its own shipping charge.</div>
          </div>

          <cart-seller-group
            v-for="(group, gi) in pricing.sellerGroups"
            :key="group.sellerId"
            :group="group"
            :box-number="gi + 1"
            :box-count="pricing.sellerGroups.length"
            :storage-url="storageUrl"
            :initial-note="sellerNotes[group.sellerId] || ''"
            :is-authenticated="isAuthenticated"
            :collapsible="foldShops"
            :start-open="groupNeedsAttention(group)"
            @remove="removeItem"
            @remove-shop="removeShop"
            @update-quantity="updateQuantity"
            @update-shipping="updateShipping"
            @add-suggestion="addSuggestion"
            @swap-alternative="swapAlternative"
          />

          <save-cart v-if="!isAuthenticated" :has-available="hasAvailableItems" />
        </div>

        <div class="col-lg-4">
          <cart-summary
            :pricing="pricing"
            :has-unavailable="pricing.unavailableItems.length > 0"
          />
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import CartSellerGroup from './components/CartSellerGroup.vue';
import CartSummary from './components/CartSummary.vue';
import EmptyCart from './components/EmptyCart.vue';
import SaveCart from './components/SaveCart.vue';

export default {
  components: { CartSellerGroup, CartSummary, EmptyCart, SaveCart },
  data() {
    return {
      loading: true,
      pricing: null,
      guestItems: [],
      storageUrl: window.__STORAGE_URL__ || STORAGE_URL,
      sellerNotes: {},
      isAuthenticated: window.__IS_AUTHENTICATED__ === true,
      isPhone: false
    };
  },
  computed: {
    foldShops() {
      return this.isPhone && !!(this.pricing && this.pricing.sellerGroups && this.pricing.sellerGroups.length > 1);
    },
    priceWentUp() {
      var changed = (this.pricing && this.pricing.priceChangedItems) || [];
      return changed.some(function (c) { return c.newPrice > c.oldPrice; });
    },
    hasAvailableItems() {
      return !!(this.pricing && this.pricing.sellerGroups && this.pricing.sellerGroups.some(function (g) {
        return (g.items || []).some(function (i) { return i.isAvailable; });
      }));
    },
    hasItems() {
      if (this.pricing && this.pricing.sellerGroups && this.pricing.sellerGroups.length > 0) return true;
      if (this.guestItems && this.guestItems.length > 0) return true;
      return false;
    }
  },
  mounted() {
    try {
      var mq = window.matchMedia('(max-width: 991.98px)');
      this.isPhone = mq.matches;
      var self = this;
      var onChange = function (e) { self.isPhone = e.matches; };
      if (mq.addEventListener) mq.addEventListener('change', onChange); else if (mq.addListener) mq.addListener(onChange);
    } catch (e) {}
    this.loadCart();
    if (this.isAuthenticated) this.loadSellerNotes();
    else this.loadGuestSellerNotes();
  },
  methods: {
    loadCart() {
      this.loading = true;
      window.LightFishCart.getItems().then(data => {
        this.pricing = data.pricing;
        this.guestItems = data.items || [];
        this.loading = false;
        this.trackSuggestionsShown();
      }).catch(() => {
        this.loading = false;
      });
    },
    loadSellerNotes() {
      fetch('/cart/seller-notes', { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : { notes: [] })
        .then(data => {
          var map = {};
          (data.notes || []).forEach(function(n) { map[n.sellerId] = n.note || ''; });
          this.sellerNotes = map;
        })
        .catch(function() {});
    },
    loadGuestSellerNotes() {
      try {
        var raw = localStorage.getItem('lightfish-guest-seller-notes');
        if (raw) {
          var parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') this.sellerNotes = parsed;
        }
      } catch (e) {}
    },
    // A box with an unavailable or repriced item opens by itself, so the problem isn't folded away.
    groupNeedsAttention(group) {
      return (group.items || []).some(function (i) { return !i.isAvailable || i.priceChanged; });
    },
    // "Remove this shop": every line from that shop, one after another, then one reload.
    removeShop(group) {
      var ids = (group.items || []).map(function (i) { return i.cartItemId; });
      var chain = Promise.resolve();
      ids.forEach(function (id) { chain = chain.then(function () { return window.LightFishCart.remove(id); }); });
      chain.then(() => { this.loadCart(); }, () => { this.loadCart(); });
    },
    removeItem(cartItemId) {
      window.LightFishCart.remove(cartItemId).then(() => {
        this.loadCart();
      });
    },
    updateQuantity(cartItemId, quantity) {
      window.LightFishCart.updateQuantity(cartItemId, quantity).then(() => {
        this.loadCart();
      });
    },
    // "Add more from this shop" (SameShopNudge): reported once per shop per page view.
    trackSameShop(e, shopId, productId, count) {
      try {
        fetch('/track/same-shop', {
          method: 'POST', credentials: 'same-origin', keepalive: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ e: e, where: 'cart', shopId: shopId || '', productId: productId || null, count: count || 0 })
        });
      } catch (x) {}
    },
    trackSuggestionsShown() {
      if (!this.pricing || !this.pricing.sellerGroups) return;
      this._sameShopSeen = this._sameShopSeen || {};
      this.pricing.sellerGroups.forEach(g => {
        if (g.suggestions && g.suggestions.length && !this._sameShopSeen[g.sellerId]) {
          this._sameShopSeen[g.sellerId] = true;
          this.trackSameShop('shown', g.sellerId, null, g.suggestions.length);
        }
      });
    },
    addSuggestion(sellerId, productId, shippingTypeId) {
      window.LightFishCart.add(productId, 1, shippingTypeId).then(r => {
        if (r && r.success) this.trackSameShop('added', sellerId, productId, 0);
        this.loadCart();
      });
    },
    // The cart's "get it from the other shop instead": add that one, drop this one, one box fewer.
    swapAlternative(alt) {
      var self = this;
      window.LightFishCart.add(alt.option.productId, 1, alt.option.shippingTypeId).then(function (r) {
        if (!r || !r.success) return;
        // Signed-in lines are removed by cart item id, guest lines by product id.
        return window.LightFishCart.remove(self.isAuthenticated ? alt.replacesCartItemId : alt.replacesProductId).then(function () {
          try {
            fetch('/cart/second-shop/track', { method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ e: 'cart_swap', productId: alt.replacesProductId, altProductId: alt.option.productId }) });
          } catch (x) {}
        });
      }).then(function () { self.loadCart(); });
    },
    updateShipping(cartItemId, shippingTypeId) {
      window.LightFishCart.updateShipping(cartItemId, shippingTypeId).then(() => {
        this.loadCart();
      });
    }
  }
};
</script>
